<template>
    <div class="team-view mt-3">
        <confirmation-modal
            :value="modalValues.show"
            :title="modalValues.title"
            :action-verb="modalValues.actionVerb"
            @ok="modalValues.action ? modalValues.action() : null"
            @cancel="modalValues.show = !modalValues.show"
        >
            <template #modal-content>
                <div v-html="modalValues.description" />
            </template>
        </confirmation-modal>

        <create-member-modal
            v-if="showAddMemberModal"
            @member-created="assignMemberHandler"
            @handle-permissions="assignMemberPermissionsHandler"
            @cancel="cancelMemberHandler"
        />

        <update-member-modal
            v-if="showUpdateMemberModal && selectedMember"
            :member="selectedMember"
            @member-updated="onMemberUpdateHandler"
            @handle-permissions="assignMemberPermissionsHandler"
            @cancel="cancelMemberUpdateHandler"
        />

        <member-access-modal
            v-if="showMemberAccessModal && selectedMember"
            :member="selectedMember"
            :entity-type="selectedEntityType"
            @permissions-saved="onPermissionsUpdated"
            @member-updated="onMemberUpdated"
            @cancel="cancelMemberPermissionsHandler"
        />

        <section class="members-section">
            <div class="row">
                <div class="col-md-12 col-lg-4">
                    <h2>Members</h2>
                    <p>
                        In this section, you can provide "Member" access to other members, allowing them to view and
                        interact with specific features of the app. This access enables them to contribute to projects,
                        share insights, and collaborate effectively while maintaining control over sensitive settings.
                    </p>
                </div>
                <div class="col-md-12 col-lg-8 team-wrapper">
                    <div v-if="!user?.ImpersonatedFromChildUser" class="mt-3 d-flex justify-content-between">
                        <b-form-input
                            v-model="searchString"
                            class="search-input-gray w-50 h-100"
                            type="text"
                            debounce="400"
                            placeholder="Search"
                        />
                        <c-button text="Add member" variant="primary" @click="openCreateModal" />
                    </div>
                    <team-table
                        style="background-color: white"
                        :members="teamMembers"
                        @edit-member="onMemberEditHandler"
                        @manage-member-entity-access="assignMemberEntitiesHandler"
                        @delete-member="toggleDeleteModal"
                    />
                    <div class="d-flex justify-content-center">
                        <b-pagination
                            v-if="totalCount / itemsPerPage > 1"
                            v-model="currentPage"
                            size="md"
                            :total-rows="totalCount"
                            :per-page="itemsPerPage"
                        />
                    </div>
                </div>
            </div>
        </section>
    </div>
</template>

<script lang="ts" setup>
import { Ref, ref, onMounted, reactive, computed, watch } from 'vue';
import TeamTable from './TeamTable.vue';
import { useLoader } from '@/composables/loader';
import CButton from '@/components/shared/CButton';
import CreateMemberModal from './CreateMemberModal.vue';
import UpdateMemberModal from './UpdateMemberModal.vue';
import ConfirmationModal from '@/components/ConfirmationModal/ConfirmationModal.vue';
import MemberAccessModal from '@/components/Team/MemberAccessModal.vue';
import { getTeamMembers, removeTeamMember } from '@/api/team';
import store from '@/store';
import { EntityType, TeamMember } from '@/api/typings/team';

const emit = defineEmits(['team-updated']);
const { execLoadedFn } = useLoader();
const teamMembers = ref<TeamMember[]>([]);
const showAddMemberModal = ref(false);
const showUpdateMemberModal = ref(false);
const showMemberAccessModal = ref(false);
const selectedElem: Ref<TeamMember | undefined> = ref();
const user = computed(() => store.state.user);
const selectedMember = ref<TeamMember | undefined>(undefined);
const selectedEntityType = ref<EntityType>(EntityType.TEMPLATE);
const searchString = ref('');
const currentPage = ref(1);
const itemsPerPage = 10;
const totalCount = ref(0);

const modalValues = reactive({
    title: 'Delete member',
    description: '',
    actionVerb: 'Delete',
    show: false,
    action: null as (() => Promise<void>) | null
});

const fetchTeamMembers = async () => {
    try {
        const from = (currentPage.value - 1) * itemsPerPage;
        const to = from + itemsPerPage;
        const membersResponse = await getTeamMembers(from, to, searchString.value);
        teamMembers.value = membersResponse.teamMembers;
        totalCount.value = membersResponse.totalCount;
    } catch (error) {
        console.error('Failed to fetch team members', error);
    }
};

const assignMemberHandler = async (member: TeamMember) => {
    execLoadedFn(async () => {
        teamMembers.value.push(member);
        showAddMemberModal.value = false;
        emit('team-updated');
    });
};

const assignMemberPermissionsHandler = async (member: TeamMember) => {
    execLoadedFn(async () => {
        selectedMember.value = member;
        showMemberAccessModal.value = true;
    });
};

const assignMemberEntitiesHandler = async (data: { member: TeamMember; entityType: EntityType }) => {
    execLoadedFn(async () => {
        selectedEntityType.value = data.entityType;
        selectedMember.value = data.member;
        showMemberAccessModal.value = true;
    });
};

const onPermissionsUpdated = async (member: TeamMember) => {
    execLoadedFn(async () => {
        selectedMember.value = member;
        showMemberAccessModal.value = false;
    });
};

const onMemberUpdated = async (updatedMember: TeamMember) => {
    execLoadedFn(async () => {
        // Update the member in the teamMembers array
        if (teamMembers.value && updatedMember) {
            const index = teamMembers.value.findIndex((member) => member.user.Id === updatedMember.user.Id);
            if (index !== -1) {
                teamMembers.value[index] = updatedMember;
            }
        }
        selectedMember.value = updatedMember;
    });
};

const onMemberUpdateHandler = async () => {
    execLoadedFn(async () => {
        showUpdateMemberModal.value = false;
        fetchTeamMembers();
    });
};

const onMemberEditHandler = async (member: TeamMember) => {
    execLoadedFn(async () => {
        selectedMember.value = member;
        showUpdateMemberModal.value = true;
    });
};

const cancelMemberHandler = async () => {
    showAddMemberModal.value = false;
};

const cancelMemberUpdateHandler = async () => {
    showUpdateMemberModal.value = false;
};

const cancelMemberPermissionsHandler = async () => {
    showMemberAccessModal.value = false;
};

const toggleDeleteModal = (elem: TeamMember) => {
    selectedElem.value = elem;
    modalValues.description = `<span>Are you sure you want to delete <strong>${elem.user.Name}</strong> member?</span>`;
    modalValues.show = !modalValues.show;
    modalValues.action = callRemoveTeamMember;
};

const callRemoveTeamMember = async () => {
    try {
        console.log(selectedElem.value?.user.Id);
        await removeTeamMember(selectedElem.value!.user.Id);
        teamMembers.value = teamMembers.value.filter((member) => member.user.Id !== selectedElem.value?.user.Id);
        emit('team-updated');
    } catch (error) {
        console.error('Failed to remove team member', error);
    }
};

const openCreateModal = () => {
    showAddMemberModal.value = true;
};

onMounted(fetchTeamMembers);
watch([searchString, currentPage], () => {
    fetchTeamMembers();
});
</script>

<style lang="scss" scoped>
.team-wrapper {
    background-color: #f7f7f7;
    border-radius: 10px;
}
.search-input-gray {
    background: var(--Gray-scale-white, #fefefe) !important;
    color: var(--Gray-scale-gray, #bfbfbf) !important;
    text-overflow: ellipsis;
}

.search-input-gray::placeholder {
    color: var(--Gray-scale-gray, #bfbfbf) !important; /* Color del placeholder */
}
</style>
